import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '../hooks/form';
import { useAuth } from '../contexts/AuthContext';
import FormInput from './FormInput';
import ConfirmationModal from './ConfirmationModal';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosInstance';

const ProfileData = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // Form for password change
    const { form: passwordForm, loading: passwordLoading, handleOnChange: handlePasswordChange, errors: passwordErrors, patch: changePassword } = useForm({
        current_password: '',
        password: '',
        password_confirmation: ''
    });

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            await changePassword(`/api/users/${user.id}/password`);
            toast.success(t('messages.passwordUpdateSuccess'));
            // Reset password form
            handlePasswordChange({ target: { name: 'current_password', value: '' } });
            handlePasswordChange({ target: { name: 'password', value: '' } });
            handlePasswordChange({ target: { name: 'password_confirmation', value: '' } });
        } catch (error) {
            console.error('Password update error:', error);
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await axiosInstance.delete(`/api/users/${user.id}`);
            toast.success(t('messages.accountDeleteSuccess'));
            // Redirect to home page after successful deletion
            window.location.href = '/';
        } catch (error) {
            console.error('Account deletion error:', error);
        }
    };

    return (
        <section className="profile py-80">
            <div className="container container-lg">
                <div className="row gy-4">
                    {/* User Info Card */}
                    <div className="col-xl-6">
                        <div className="px-24 py-40 border border-gray-100 hover-border-main-600 transition-1 rounded-16 tw-h-full">
                            <h6 className="mb-32 text-xl">{t('profile.personalInfo')}</h6>
                            <div className="mb-24">
                                <label className="mb-8 text-lg text-neutral-900 fw-medium">
                                    {t('common.name')}
                                </label>
                                <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #ccc', backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}>
                                    {user?.name || ''}
                                </div>
                            </div>
                            <div className="mb-24">
                                <label className="mb-8 text-lg text-neutral-900 fw-medium">
                                    {t('common.email')}
                                </label>
                                <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #ccc', backgroundColor: '#f0f0f0', cursor: 'not-allowed' }}>
                                    {user?.email || ''}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Password Change Card */}
                    <div className="col-xl-6">
                        <div className="px-24 py-40 border border-gray-100 hover-border-main-600 transition-1 rounded-16">
                            <h6 className="mb-32 text-xl">{t('profile.changePassword')}</h6>
                            <form onSubmit={handlePasswordSubmit} noValidate>
                                <FormInput
                                    label={t('profile.currentPassword')}
                                    type="password"
                                    name="current_password"
                                    value={passwordForm.current_password}
                                    onChange={handlePasswordChange}
                                    errors={passwordErrors?.current_password}
                                    required
                                />
                                <FormInput
                                    label={t('common.password')}
                                    type="password"
                                    name="password"
                                    value={passwordForm.password}
                                    onChange={handlePasswordChange}
                                    errors={passwordErrors?.password}
                                    required
                                />
                                <FormInput
                                    label={t('common.confirmPassword')}
                                    type="password"
                                    name="password_confirmation"
                                    value={passwordForm.password_confirmation}
                                    onChange={handlePasswordChange}
                                    errors={passwordErrors?.password_confirmation}
                                    required
                                />
                                <div className="mt-48">
                                    <button 
                                        type="submit" 
                                        className="px-40 py-18 bg-main-600 tw-text-white tw-rounded-md hover-bg-main-700 tw-transition-colors tw-duration-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                                        disabled={passwordLoading}
                                    >
                                        {passwordLoading ? t('common.saving') : t('common.save')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                    
                    {/* Account Deletion Section */}
                    <div className="mt-4 col-xl-12">
                        <div className="px-24 py-40 tw-border tw-border-red-500 rounded-16">
                            <h6 className="mb-32 text-xl tw-text-red-600">{t('profile.deleteAccount')}</h6>
                            <p className="mb-24 text-gray-500">{t('profile.deleteAccountWarning')}</p>
                            <button 
                                type="button" 
                                className="px-40 py-18 tw-bg-red-600 tw-text-white tw-rounded-md hover:tw-bg-red-700 tw-transition-colors tw-duration-200"
                                onClick={() => setIsDeleteModalOpen(true)}
                            >
                                {t('profile.deleteAccount')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Account Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteAccount}
                title={t('profile.deleteAccount')}
                message={t('profile.deleteAccountWarning')}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
            />
        </section>
    );
};

export default ProfileData; 